import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { FC, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { projectApi } from 'src/api/projectApi';
import { Tabs } from 'src/common/tabs/tabs';
import useGenericStyles from 'src/styles/useGenericStyles';
import setTitle from 'src/utils/setTitle';
import SimpleBreadCrumb from 'src/components/widgets/BreadCrumb/SimpleBreadCrumb';

const ProjectDetails: FC = () => {
  const classes = useGenericStyles();
  const { projectID, frsID } = useParams<{ projectID: string; frsID?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // const { user } = useAuth();
  // const companyId = user?.organization?.id;
  const [projectDetails, setProjectDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  setTitle('ITSM | Project Details');

  const getProjectDetails = async () => {
    try {
      setLoading(true);
      const projDetails = await projectApi.getProjectDetailsById(projectID);
      if (projDetails?.data) setProjectDetails(projDetails.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectID) {
      getProjectDetails();
    }
  }, [projectID]);

  const activeTab = useMemo(() => {
    if (!projectID) return 'cases';
    const frsListPath = `/projects/${projectID}/frs`;
    if (location.pathname === frsListPath) return 'frs';
    return 'cases';
  }, [location.pathname, projectID]);

  const handleTabChange = (_event: unknown, newValue: string) => {
    if (!projectID) return;
    if (newValue === 'frs') navigate(`/projects/${projectID}/frs`);
    else navigate(`/projects/${projectID}/cases`);
  };

  const tabs = [
    { label: 'Cases', value: 'cases' },
    { label: 'FRS', value: 'frs' },
  ];

  const breadcrumbData = {
    links: [
      {
        name: 'Projects',
        to: '/projects',
      },
      {
        name: projectDetails?.name,
        to: `/projects/${projectID}/cases`,
      },
    ],
  };

  if (frsID) {
    return <Outlet />;
  }

  if (loading && !projectDetails) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container
      maxWidth={false}
      className={classes.container}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}>
      {/* Breadcrumb */}
      {breadcrumbData && (
        <Box flexShrink={0} style={{ marginBottom: '12px' }}>
          <SimpleBreadCrumb data={breadcrumbData} />
        </Box>
      )}

      {/* Title Row */}
      <Box
        flexShrink={0}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          marginBottom: '16px',
        }}>
        {/* Title */}
        {projectDetails?.name ? (
          <Typography
            variant="h5"
            style={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#1A202C',
              whiteSpace: 'nowrap',
            }}>
            {projectDetails?.name}
          </Typography>
        ) : (
          <Skeleton variant="text" width={200} height={32} />
        )}
      </Box>

      <Box flexShrink={0}>
        <Tabs
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          tabs={tabs}
          isTransform={false}
        />
      </Box>
      <Outlet />
    </Container>
  );
};

export default ProjectDetails;
